import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Sections whose last-visited sub-route we want to remember.
 * Only sections with meaningful child routes are tracked.
 */
const TRACKED_SECTIONS = [
  '/proyectos',
  '/cotizaciones',
  '/ordenes',
  '/clientes',
] as const

const KEY_PREFIX = 'route_memory_'

function getSectionFor(pathname: string): string | null {
  for (const section of TRACKED_SECTIONS) {
    if (pathname === section || pathname.startsWith(section + '/')) {
      return section
    }
  }
  return null
}

/**
 * Hook that remembers the last visited route per sidebar section.
 * Data lives in sessionStorage — cleared on tab close or logout.
 *
 * Usage in Sidebar:
 *   const { getLastRoute } = useRouteMemory()
 *   <NavLink to={getLastRoute('/proyectos')} ...>
 */
export function useRouteMemory() {
  const location = useLocation()

  // Track current route
  useEffect(() => {
    const section = getSectionFor(location.pathname)
    if (section) {
      sessionStorage.setItem(KEY_PREFIX + section, location.pathname)
    }
  }, [location.pathname])

  const getLastRoute = useCallback((basePath: string): string => {
    const saved = sessionStorage.getItem(KEY_PREFIX + basePath)
    return saved || basePath
  }, [])

  return { getLastRoute }
}

/** Call on logout to clear all route memory */
export function clearRouteMemory() {
  for (const section of TRACKED_SECTIONS) {
    sessionStorage.removeItem(KEY_PREFIX + section)
  }
}
